/// <reference path="../../app.definition.ts" />

import angular, { ILocationService, IQService, ITimeoutService, IWindowService } from "angular";
import { PublicClientApplication } from '@azure/msal-browser';
import _ from "lodash";
import moment from "moment";
import { PermissionsEnum, PermissionService, TargetEnum } from "ecv/common/permissions";
import { User } from '@/app/common/model/user';
import { CacheService } from '@/app/common/service/cache.service';
import { JavaApiAdapterService } from '@/app/common/apiadapter/javaapi.service';
import { PopUpService } from '@/app/common/popup/popup.service';
import { IStateService } from 'angular-ui-router';
import { TeamPermissionService } from '@/app/common/service/teamPermission.service';
import { PasswordCollectorService } from '@/app/common/passwordCollector/services/passwordCollector.service';
import { SubjectService } from '@/app/common/service/subject.service';
import { EnterpriseService } from '@/app/common/service/enterprise.service';
import { IAppRootScopeService } from '@/app/app.definition';

export class AuthenticationService {
    token: string;
    loginCallback: Function;
    refreshCredentials: Function;
    loginExpiredPassReset: Function;
    loginComplete;
    currentPlan;
    CACHE_KEY_MSAL_RESPONSE = 'msal-res';
    MSAL_APP = 'ecvAuth';
    username = '';
    scopes = ["openid", "offline_access"];
    refreshCredTimeout;
    userEmail = '';
    loadLandingPage: Function;

    static $inject = ["$rootScope", "ApiAdapterService", "CacheService", "PopUpService", "$state", "PermissionService",
        "$q", "TeamPermissionService", "PasswordCollectorService", "$window", 'authConfig', '$timeout', '$location', 'SubjectService', 'EnterpriseService', 'ecareEnv'];
    constructor(
        private $rootScope: IAppRootScopeService,
        private ApiAdapterService: JavaApiAdapterService,
        private cache: CacheService,
        private PopUpService: PopUpService,
        private $state: IStateService,
        private permService: PermissionService,
        $q: IQService,
        $_teamPerm: TeamPermissionService,
        PasswordCollectorService: PasswordCollectorService,
        private $window: IWindowService,
        private authConfig: any,
        private $timeout: ITimeoutService,
        private $location: ILocationService,
        private SubjectService: SubjectService,
        private EnterpriseService: EnterpriseService,
        private ecareEnv: string
    ) {
        this.refreshCredentials = (isReloadScenario = false) => {
            const deferred = $q.defer();
            const authRes = this.cache.getAuthResponse();
            // Open IndexDB for the permissions
            const initializePermissions = () => {
                let user = this.cache.getUser();
                if (!user || !user.partyId) {
                    console.error("Cannot initialize permissions - no cached user partyId available.");
                    return;
                }
                this.permService.initialize(user.partyId);
            }
            const refreshCred = () => {
                console.log("Refreshing the Credentials........")
                // The cached user is already available, and initialize() only seeds the party id
                // and opens IndexDB - nothing here depends on the new token. Seeding it up front
                // keeps permission lookups valid for pages that ui-router restores while
                // acquireTokenSilent is still in flight (e.g. Teams after a hard refresh).
                initializePermissions();
                window[this.MSAL_APP].acquireTokenSilent({
                    scopes: this.scopes,
                    account: this.$window[this.MSAL_APP].getAllAccounts()[0],
                    forceRefresh: true
                }).then((res) => {
                    this.cache.setAuthResponse(res);
                    this.cache.setAuthToken(res.idToken);
                    this.addSASSToken();
                    this.setRefreshCredentialTimeOut(45);
                    console.log("Credentials Refreshed.......", res.idToken);
                    deferred.resolve();
                }).catch(err => {
                    deferred.reject();
                    this.signOut(); // fall back if refresh token fails
                });
            }

            if (authRes && authRes.idTokenClaims.exp) { //condition required to check expiration
                let now = moment();
                let exp = moment(authRes.idTokenClaims.exp * 1000, 'x');
                let diff = moment.duration(exp.diff(now));
                if (diff.asMinutes() > 45) {
                    initializePermissions();
                    // if difference in minutes between expired time and current time is greater than 45m i.e.
                    // Since session started less then 15m has been lapsed and no need to refresh token
                    if (isReloadScenario && $state.current.name === "") {
                        // refreshCredTimeout is getting cleared in case of reloading scenarios
                        // So setting it again from here for remaining minutes
                        this.setRefreshCredentialTimeOut(diff.asMinutes() - 15);
                    } else {
                        deferred.resolve();
                    }
                } else {
                    refreshCred();
                }

            } else {
                const token = this.cache.getAuthToken();
                if (!token && authRes) {
                    refreshCred();
                } else {
                    this.signOut();
                }
            }
            return deferred.promise;
        }

        //This contains all promises that need to be resolved before loading the carefeed page
        this.loadLandingPage = (userInfo) => {
            let deferred = $q.defer();
            let promises = [];
            promises.push(this.permService.initialize(userInfo.partyId).then((dbCreated) => {
                return this.permService.getPermissionsForMe().then(() => {
                    return this.permService.getPermissionsForTarget(TargetEnum.TEAM, [userInfo.partyId]).then(data => {
                        if (Array.isArray(data) && data.length > 0) {
                            let allow = data[0][TargetEnum.TEAM][PermissionsEnum.CREATE] || data[0][TargetEnum.TEAM][PermissionsEnum.CREATE_ON_BEHALF]
                            cache.set('isCreateTeamAllow', allow, true);
                            this.$rootScope.canUsrCreateTeam = allow;
                            return;
                        } else {
                            return;
                        }
                    })

                });
            }));
            promises.push(this.SubjectService.checkUserMembership(userInfo.id));
            promises.push(this.EnterpriseService.init());
            promises.push(ApiAdapterService.getUserPermissions().then((p) => {
                cache.setUserTeamPermissions(p);
                $_teamPerm.init(p);
                return;
            }))
            // call userExperience api if response from identity has the flag “userExperience“ true.
            if (!!userInfo.userExperience) {
                //cache the response of the API
                promises.push(ApiAdapterService.getUserExperience().then((p) => {
                    cache.setUserExperience(p);
                    return;
                }))
            }
            $q.all(promises).then(() => {
                deferred.resolve();
            });

            return deferred.promise;
        }

        this.loginComplete = (loginRes) => {
            const deferred = $q.defer();
            //open call
            ApiAdapterService.init((error) => {
                PopUpService.openInformationModal(error);
                $state.go('login');
            });

            let timeoutCancelled = false;

            let oldB = !String.prototype.includes;
            setTimeout(function () {
                if (!timeoutCancelled) {
                    let reason = oldB ? `Oops! We've found compatibility issues` : `Oops! We seem to be experiencing a slowdown`;
                    let expl = oldB ? `
                    Using Internet Explorer 11 or an older browser?<br/>
                    Please try
                    <a target="_blank" style="color:${eCareVault.Util.COLORS.ecvOrange}" href="https://www.google.com/chrome/browser/desktop/index.html">Google Chrome</a>,
                    <a target="_blank" style="color:${eCareVault.Util.COLORS.ecvOrange}" href="https://www.mozilla.org/firefox‎">Mozilla Firefox</a>,
                    or <a target="_blank" style="color:${eCareVault.Util.COLORS.ecvOrange}" href="https://www.microsoft.com/en-us/windows/microsoft-edge">Microsoft Edge</a>
                    instead.
                ` : `
                    The server is taking too long to respond. Please log in again or
                    <a target="_blank" style="color:${eCareVault.Util.COLORS.ecvOrange}" href="https://` + this.ecareEnv + `.ecarevault.com/#/ecare-vault/contact">contact us</a>
                    if the issue persists.
                `;

                    let msg = `
                    <h4 style="text-align:left;color:${eCareVault.Util.COLORS.ecvBlue};margin-top: -15px;">` + reason + `</h4>
                    <p style="text-align:left;margin-bottom: -15px;">` + expl + `</p>
                `;

                    PopUpService.openInformationModal(msg);
                    $rootScope.loginShowLoader = false;
                    $state.go('login');
                }
            }, oldB ? 1000 : 60000);


            timeoutCancelled = true;

            this.currentPlan = cache.getPInfo();
            if (!this.currentPlan) {
                this.currentPlan = {};
                ApiAdapterService.getCurrentSubscribePlanForUser(true).then(
                    (s) => {
                        this.currentPlan.planType = s.planType,
                            this.currentPlan.maxCR = s.maxCR;
                        cache.setPInfo(this.currentPlan);
                    },
                    e => {
                        console.log('error while fetching current subscription plans');
                        console.log(e);
                    }
                );
            }

            ApiAdapterService.getIdentity().then((userInfo: User) => {
                deferred.resolve(true);
                $rootScope.callPendingInvAlert = true;
                cache.setUser(userInfo);
                cache.set('userOrgName', userInfo.organizationName);
                cache.setUserSignature(userInfo.userSignature);
                //Add the user's notification setting in the cache.
                cache.setNotificationDisabledFlag(userInfo.isNotificationDisabled);
                let prm = this.loadLandingPage(userInfo);
                if (!!cache.getToken()) {
                    $state.go('password-reset');
                } else if (cache.getUser().policies.length > 0 && !!cache.getUser().passwordSet) {
                    let crntSubject = cache.getCurrentSubject();
                    if (!!crntSubject)
                        crntSubject = crntSubject.subjectId
                    if (userInfo.organizationId == 0) {
                        //check if user has any current services
                        ApiAdapterService.getAssignedService(crntSubject).then((s) => {
                            if (s.totalElements > 0) {
                                cache.setIsServicesAssigned(true);
                            } else {
                                cache.setIsServicesAssigned(false);
                            }
                        });
                        //check if user has any Previous services
                        ApiAdapterService.getPreviousServices(crntSubject, cache.getUser().userId).then((s) => {
                            if (!!s && s.totalElements > 0) {
                                cache.setIsPreviousService(true);
                            } else {
                                cache.setIsPreviousService(false);
                            }
                        });
                    }
                    //As soon as the user logs in fetch all the documents and cache it.
                    ApiAdapterService.checkIfVaultKeyResetRequired().then((resetVaultKey) => {
                        cache.setVaultKeyResetRequiredMessage(resetVaultKey.message);
                        cache.setVaultKeyResetRequired(resetVaultKey.isResetRequired);
                        PasswordCollectorService.openPasswordModal();
                        prm.then(() => {
                            $state.go('parent-carefeed');
                        });
                    }, (e) => {
                        console.log(e, 'Error in chcking if vault key has expired');
                        deferred.resolve([]);
                    })
                } else if (!cache.getUser().id) {
                    //the user's identity call has failed but the status code was 200 for some reason (EC2)
                    const text = (!!loginRes && !!loginRes.given_name) ? 'Hi' + ' ' + loginRes.given_name + ',' : ',';
                    PopUpService.openInformationModal(text + 'Looks like you have not signed up yet on eCare Vault.').then(
                        success => '',
                        error => ''
                    );
                } else {
                    $state.go("parent-dashboard")
                }
            }, (error) => {
                $rootScope.callPendingInvAlert = true;
                if (error && !error.status) {
                    deferred.reject(error);
                }
                if (!error || (!!error.status && error.status == 404)) {
                    const user = new User().deserialize({
                        providerIdentity: loginRes.account.idTokenClaims.email,
                        firstName: loginRes.account.given_name
                    })
                    this.cache.setUser(user);
                    const tempInvitationCode = this.$window.sessionStorage["ecvInvite"];
                    //if the the token is present and the user is not active
                    //set the temp user
                    ApiAdapterService.checkToken(tempInvitationCode).then((s) => {
                        //set the validation data
                        cache.setTokenValidation(s);
                        //redirect to sign up page
                        $state.go('signup-provider', { 'for': 'teammember' });
                    });
                }
            });

            return deferred.promise;
        }
    }

    init = () => {
        if (!this.$window[this.MSAL_APP]) {
            try {
                const msalConfig = {
                    auth: {
                        clientId: this.authConfig.clientId,
                        authority: `${this.authConfig.authorityBase}/${this.authConfig.signInSignUpPolicy}/`,
                        knownAuthorities: this.authConfig.knownAuthorities,
                        redirectUri: window.location.origin,
                        code_challenge: this.authConfig.codeChallenge,
                        code_challenge_method: this.authConfig.codeChallengeMethod,
                        code_verifier: this.authConfig.codeVerifier
                    },
                    cache: {
                        cacheLocation: "sessionStorage",
                        storeAuthStateInCookie: false
                    },
                    system: {
                        iframeHashTimeout: 30000
                    }
                };

                this.$window[this.MSAL_APP] = new PublicClientApplication(msalConfig);
                // Redirect: once login is successful and redirects with tokens, call Graph API
                this.$window[this.MSAL_APP].handleRedirectPromise().then(this.handleResponse).catch(err => {
                    this.signOut();
                    console.error(err);
                });
            } catch (e) {
                console.error('Authentication configuration is not set or is invalid.', e);
            }
        } else {
            this.handleResponse(null);
        }
    }

    setRefreshCredentialTimeOut = (minute) => {
        this.$timeout.cancel(this.refreshCredTimeout);
        this.refreshCredTimeout = this.$timeout(() => {
            const passwordModal = angular.element('body').find('.password-modal');
            if (passwordModal.length == 0) {
                // this condition means the session is active then and then do the refresh credentials
                this.refreshCredentials();
            }
        }, minute * 60 * 1000);
    }

    logggedIn = (data) => {
        this.cache.setAuthResponse(data);
        this.setRefreshCredentialTimeOut(45);
        this.username = (data.account || data).username;
        this.cache.setAuthType((data.idTokenClaims.idp === 'google.com' ? 'google' : (data.idTokenClaims.idp === 'linkedin.com' ? 'linkedin' : 'azure')))
        this.cache.setAuthToken(data.idToken);
        this.loginComplete(data).catch(e => {
            this.$rootScope.rootShowLoader = false;
            let msg = `Something went wrong. Please log in again.`;
            this.PopUpService.openInformationModal(msg).then((s) => {
                this.$rootScope.logout();
            });
        });
        this.addSASSToken();
    }

    handleResponse = (resp) => {
        const lh = this.$location.search().lh;    //loginHint if coming from safe passage
        if (!!lh) {
            this.userEmail = atob(lh);
            this.cache.set('lh', lh);
        }
        if (resp !== null) {
            localStorage.removeItem("status");
            this.$window[this.MSAL_APP].setActiveAccount(
                this.$window[this.MSAL_APP].getAccountByHomeId(resp.account.homeAccountId)
            );
            this.logggedIn(resp);
        } else {
            const currentAccounts = this.$window[this.MSAL_APP].getAllAccounts();
            if (currentAccounts == null) {
                return;
            } else if (currentAccounts.length > 1) {
                // Add choose account code here
                console.warn("Multiple accounts detected.");
            } else if (currentAccounts.length === 1) {
                this.refreshCredentials(true);
            } else if (currentAccounts.length == 0) {
                this.signIn();
            }
        }
    }

    signIn = () => {
        if (!!this.userEmail) {
            const loginRequest = {
                scopes: this.scopes,
                prompt: 'none',
                loginHint: this.userEmail
            };
            this.$window[this.MSAL_APP].loginRedirect(loginRequest);
        } else {
            this.userEmail = null;
            const loginRequest = {
                scopes: this.scopes,
                prompt: 'login'
            };
            this.$window[this.MSAL_APP].loginRedirect(loginRequest);
        }
    }

    signOut = () => {
        const logoutRequest = {
            account: this.$window[this.MSAL_APP].getAllAccounts()[0]
        };
        const status = localStorage.getItem("status");
        //If coming from Safe Passage
        if (!_.isEmpty(this.userEmail)) {
            //redirect back to safe passage on logout
            logoutRequest['postLogoutRedirectUri'] = this.authConfig.safePassageUri;
            //Set user status to inactive in safe passage
            let frame = document.getElementById('ifrm-download') as HTMLIFrameElement;
            frame.src = this.authConfig.signoutSafePassage;
            setTimeout(() => {
                if (frame.contentWindow && !status) {
                    localStorage.setItem("status", 'inactive'); //if ecv has been opened in more than one tab from safe passages then this will help to log out from all the tabs
                    frame.contentWindow.postMessage({ "status": 'inactive' }, this.authConfig.safePassageUri);
                }
            }, 1000);
            ////// TO-DO Handle logout for LEAP////////
        }
        setTimeout(() => {
            this.$window[this.MSAL_APP].logout(logoutRequest);
        }, 1000);

    }

    addSASSToken = () => {
        this.ApiAdapterService.getSASSToken().then((token) => {
            this.cache.setSASSToken(token);
        });
    }
}


angular.module('eCareVault.authentication')
    .service('AuthenticationService', AuthenticationService);
